import { Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUpdateAnonymizerConfig, useAnonymizerConfig } from '@/entities/anonymizer-config';
import { ConfigFormLayout } from './ConfigFormLayout';

type FieldMapping = {
  fullRegexKey: string;
  partialRegexKey: string;
};

const CLASS_FIELD_MAP: Record<string, FieldMapping> = {
  snils: { fullRegexKey: 'fullSnilsRegex', partialRegexKey: 'partialSnilsRegex' },
  inn: { fullRegexKey: 'fullInnRegex', partialRegexKey: 'partialInnRegex' },
  'bank-card': { fullRegexKey: 'fullCardRegex', partialRegexKey: 'partialCardRegex' },
  'bank-account': { fullRegexKey: 'fullAccountRegex', partialRegexKey: 'partialAccountRegex' },
};

const CLASS_LABEL_MAP: Record<string, 'classes.snils' | 'classes.inn' | 'classes.bankCard' | 'classes.bankAccount'> = {
  snils: 'classes.snils',
  inn: 'classes.inn',
  'bank-card': 'classes.bankCard',
  'bank-account': 'classes.bankAccount',
};

type ConfigData = Record<string, unknown>;

type FormValues = {
  fullRegex: string;
  contextMarkers: string;
  lookbackWindow: string;
  partialRegex: string;
  partialContextMarkers: string;
  partialLookbackWindow: string;
  otherClassContextMarkers: string;
  validationRegex: string;
};

const getDefaultValues = (config: ConfigData | undefined, fieldMapping: FieldMapping | undefined): FormValues => {
  const getArr = (key: string): string[] => (config?.[key] as string[]) ?? [];
  const getStr = (key: string): string => (config?.[key] as string) ?? '';
  const getNum = (key: string): string => String(config?.[key] ?? '');

  return {
    fullRegex: fieldMapping ? getStr(fieldMapping.fullRegexKey) : '',
    contextMarkers: getArr('contextMarkers').join('\n'),
    lookbackWindow: getNum('lookbackWindow'),
    partialRegex: fieldMapping ? getStr(fieldMapping.partialRegexKey) : '',
    partialContextMarkers: getArr('partialContextMarkers').join('\n'),
    partialLookbackWindow: getNum('partialLookbackWindow'),
    otherClassContextMarkers: getArr('otherClassContextMarkers').join('\n'),
    validationRegex: getStr('validationRegex'),
  };
};

type Props = {
  classId: string;
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const FinancialDocConfigForm: FC<Props> = ({ classId, data, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const config = data as ConfigData | undefined;
  const { mutate: updateConfig, isPending } = useUpdateAnonymizerConfig();
  const { refetch } = useAnonymizerConfig(classId);
  const fieldMapping = CLASS_FIELD_MAP[classId];
  const labelKey = CLASS_LABEL_MAP[classId as keyof typeof CLASS_LABEL_MAP];

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(config, fieldMapping),
  });

  useEffect(() => {
    if (config) {
      reset(getDefaultValues(config, fieldMapping));
    }
  }, [config, fieldMapping, reset]);

  useEffect(() => {
    onUnsavedChange?.(isDirty);
  }, [isDirty, onUnsavedChange]);

  const onSubmit = handleSubmit((values) => {
    if (!config || !fieldMapping) return;
    const body: ConfigData = {
      [fieldMapping.fullRegexKey]: values.fullRegex || config[fieldMapping.fullRegexKey],
      contextMarkers: values.contextMarkers ? values.contextMarkers.split('\n').filter(Boolean) : config.contextMarkers,
      lookbackWindow: values.lookbackWindow ? Number(values.lookbackWindow) : config.lookbackWindow,
      [fieldMapping.partialRegexKey]: values.partialRegex || config[fieldMapping.partialRegexKey],
      partialContextMarkers: values.partialContextMarkers
        ? values.partialContextMarkers.split('\n').filter(Boolean)
        : config.partialContextMarkers,
      partialLookbackWindow: values.partialLookbackWindow
        ? Number(values.partialLookbackWindow)
        : config.partialLookbackWindow,
      otherClassContextMarkers: values.otherClassContextMarkers
        ? values.otherClassContextMarkers.split('\n').filter(Boolean)
        : config.otherClassContextMarkers,
      validationRegex: values.validationRegex || config.validationRegex,
    };
    updateConfig({ className: classId, body });
  });

  const handleReset = () => {
    reset(getDefaultValues(config, fieldMapping));
    refetch();
  };

  return (
    <ConfigFormLayout
      title={labelKey ? t(labelKey) : classId}
      isDirty={isDirty}
      onSave={onSubmit}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6 [&_label]:text-foreground">
        {/* Обнаружение полного номера */}
        <h3 className="text-lg font-medium">{t('fields.fullNumberDetection')}</h3>
        <Textarea
          control={control}
          name="fullRegex"
          label={t('fields.fullMatchPattern')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="contextMarkers"
          label={t('fields.contextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="lookbackWindow"
          label={t('fields.lookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />

        {/* Обнаружение частичного номера */}
        <h3 className="text-lg font-medium">{t('fields.partialNumberDetection')}</h3>
        <Textarea
          control={control}
          name="partialRegex"
          label={t('fields.partialMatchPattern')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="partialContextMarkers"
          label={t('fields.partialContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="partialLookbackWindow"
          label={t('fields.partialLookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />

        {/* Исключения и валидация */}
        <h3 className="text-lg font-medium">{t('fields.exceptionsAndValidation')}</h3>
        <Textarea
          control={control}
          name="otherClassContextMarkers"
          label={t('fields.otherClassContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="validationRegex"
          label={t('fields.validationPattern')}
          disabled={!canEdit}
          rows={3}
        />
      </div>
    </ConfigFormLayout>
  );
};
